<template>
  <div class="wallet-connect-container">
    <a
      href="#"
      class="wallet-connect-link"
      @click.prevent="toggleDropdown"
      :class="{ 'wallet-connected': connected }"
    >
      <i class="fas fa-wallet me-1"></i>
      <span v-if="!connected">Connect Wallet</span>
      <span v-else class="font-weight-bold">{{ shortAddress }}</span>
      <i
        v-if="connected"
        :class="[
          'fas',
          'ms-1',
          verificationStatus === 'verified'
            ? 'fa-check-circle text-success'
            : 'fa-circle',
          verificationStatus === 'error' ? 'text-danger' : '',
        ]"
        style="font-size: 10px; vertical-align: top"
      ></i>
    </a>

    <div v-if="showDropdown" class="wallet-dropdown">
      <div class="wallet-dropdown-header">
        <div v-if="connected" class="wallet-header-content">
          <div class="wallet-header-left">
            <div class="address-container">
              <span class="wallet-address">{{ shortAddress }}</span>
              <i
                v-if="verificationStatus === 'verified'"
                class="fas fa-check-circle text-primary ms-1"
                title="Verified wallet"
              ></i>
              |
              <span
                class="network-badge"
                :class="
                  networkIsCorrect
                    ? 'network-badge-success'
                    : 'network-badge-warning'
                "
              >
                {{ networkName }}
              </span>
            </div>
          </div>
          <button
            @click="disconnectWallet"
            class="header-disconnect-btn"
            title="Disconnect Wallet"
          >
            <i class="fas fa-power-off"></i>
          </button>
        </div>
        <div v-else class="wallet-dropdown-title">Connect your wallet</div>
      </div>

      <div class="wallet-dropdown-body">
        <div v-if="!connected">
          <div class="wallet-selection">
            <div
              class="wallet-option"
              @click="initiateConnectWallet('ethereum')"
              :class="{ 'wallet-option-disabled': !hasMetaMask }"
            >
              <i class="fa-brands fa-ethereum wallet-icon"></i>
              <div class="wallet-option-info">
                <span class="wallet-name">MetaMask</span>
                <span v-if="!hasMetaMask" class="wallet-status-error"
                  >Not installed</span
                >
                <span v-else class="wallet-status">Connect</span>
              </div>
            </div>

            <div
              class="wallet-option"
              @click="initiateConnectWallet('solana')"
              :class="{ 'wallet-option-disabled': !hasPhantom }"
            >
              <i class="fas fa-ghost wallet-icon"></i>
              <div class="wallet-option-info">
                <span class="wallet-name">Phantom</span>
                <span v-if="!hasPhantom" class="wallet-status-error"
                  >Not installed</span
                >
                <span v-else class="wallet-status">Connect</span>
              </div>
            </div>
          </div>

          <div class="wallet-message">
            <p v-if="!hasMetaMask && !hasPhantom" class="wallet-no-wallets">
              <i class="fas fa-exclamation-circle text-warning"></i>
              No supported wallets detected. Please install
              <a href="https://metamask.io/" target="_blank">MetaMask</a> or
              <a href="https://phantom.app/" target="_blank">Phantom</a>.
            </p>
            <p v-else class="wallet-description text-center">
              Connect to verify your wallet ownership and interact with the
              blockchain.
            </p>
          </div>
        </div>

        <div v-else>
          <div class="row g-3">
            <div class="col-md-5">
              <!-- Balances Section -->
              <div class="wallet-card balances-card">
                <div class="wallet-card-header">
                  <h6>Your Balances</h6>
                  <button
                    class="refresh-icon-btn"
                    @click="fetchBalances"
                    :disabled="isLoadingBalances"
                    title="Refresh Balances"
                  >
                    <i
                      class="fas fa-sync-alt"
                      :class="{ 'fa-spin': isLoadingBalances }"
                    ></i>
                  </button>
                </div>
                <div class="wallet-card-body">
                  <div class="balance-item">
                    <div class="balance-icon-wrapper">
                      <i class="fa-brands fa-ethereum balance-icon"></i>
                    </div>
                    <div class="balance-details">
                      <div class="balance-label">ETH</div>
                      <div class="balance-value">{{ ethBalance }}</div>
                    </div>
                  </div>
                  <div class="balance-item">
                    <div class="balance-icon-wrapper">
                      <img
                        :src="require('@/assets/img/icon.png')"
                        alt="XAP"
                        class="balance-icon img-fluid"
                        style="border-radius: 50%"
                      />
                    </div>
                    <div class="balance-details">
                      <div class="balance-label">XAP</div>
                      <div class="balance-value">{{ tokenBalance }}</div>
                    </div>
                  </div>

                  <!-- External Withdrawable Balance Section -->
                  <div
                    v-if="externalWithdrawableBalance > 0"
                    class="claim-alert px-4 mt-4"
                  >
                    <div class="claim-details">
                      <div class="claim-amount">
                        +{{ externalWithdrawableBalance }}
                        <span class="currency">XAP</span>
                      </div>
                      <div class="claim-message">
                        These funds have been processed and are ready to claim.
                      </div>
                      <a
                        href="https://claim.exaprotocol.com"
                        target="_blank"
                        class="btn btn-link btn-sm mb-0 px-0"
                      >
                        <i class="fas fa-external-link-alt me-1"></i> Claim XAP
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-7">
              <!-- XAP Withdrawal Section (when connected and verified) -->
              <div
                v-if="verificationStatus === 'verified'"
                class="wallet-card withdrawal-card"
              >
                <div class="wallet-card-header">
                  <h6>XAP Withdrawal</h6>
                  <button
                    class="refresh-icon-btn"
                    @click="fetchWithdrawableAmount"
                    :disabled="isLoadingWithdrawal"
                    title="Refresh Withdrawal Info"
                  >
                    <i
                      class="fas fa-sync-alt"
                      :class="{ 'fa-spin': isLoadingWithdrawal }"
                    ></i>
                  </button>
                </div>

                <div class="wallet-card-body">
                  <div v-if="isLoadingWithdrawal" class="loading-indicator">
                    <i class="fas fa-circle-notch fa-spin me-1"></i> Loading...
                  </div>

                  <div v-else-if="withdrawalError" class="error-message">
                    <h3 class="text-danger">
                      <i class="fas fa-exclamation-circle me-1"></i>
                    </h3>

                    {{ withdrawalError }}

                    <div
                      v-if="isWithdrawalOnCooldown"
                      class="cooldown-timer-container"
                    >
                      <span v-if="isWithdrawalOnCooldown">
                        Next withdrawal in
                        <div class="countdown-timer">
                          <div class="countdown-unit">
                            <span class="time">{{
                              countdownTime.split(":")[0]
                            }}</span>
                            <span class="label">hrs</span>
                          </div>
                          <div class="countdown-separator">:</div>
                          <div class="countdown-unit">
                            <span class="time">{{
                              countdownTime.split(":")[1]
                            }}</span>
                            <span class="label">min</span>
                          </div>
                          <div class="countdown-separator">:</div>
                          <div class="countdown-unit">
                            <span class="time">{{
                              countdownTime.split(":")[2]
                            }}</span>
                            <span class="label">sec</span>
                          </div>
                        </div>
                      </span>
                      <span
                        v-else-if="countdownTime === 'Available now'"
                        class="next-withdrawal-time"
                      >
                        {{ countdownTime }}
                      </span>
                      <span v-else class="next-withdrawal-time">
                        Next withdrawal: {{ nextWithdrawalTime }}
                      </span>
                    </div>
                  </div>

                  <div v-else class="withdrawal-info">
                    <div v-if="!withdrawalSuccess" class="info-grid">
                      <div
                        class="info-item"
                        v-tooltip.top="{
                          value: `
                           
                                Also Includes:
                                <br/>
                                Mobile Node Storage:
                                <span class='fw-bold text-end'>${
                                  withdrawalData.nodeXcpPoints || 0
                                } xCP</span>
                                 <br/>
                                XAPTapBot:
                                <span class='fw-bold text-end'>${
                                  withdrawalData.tgTapEarningsToXCP || 0
                                } xCP</span>
                              
                              
                            
                          `,
                          autoHide: false,
                          escape: false,
                          pt: {
                            text: 'earnings-tooltip',
                          },
                        }"
                      >
                        <div class="info-label">xCP Earned:</div>
                        <div class="info-value">
                          {{ Math.round(withdrawalData.earnedXCP) }}
                          xCP
                        </div>
                      </div>
                      <div class="info-item">
                        <div class="info-label">xCP Redeemed:</div>
                        <div class="info-value">
                          {{ Math.round(withdrawalData.redeemedXCP) }}
                          xCP
                        </div>
                      </div>
                      <div class="info-item">
                        <div class="info-label">xCP Redeemable:</div>
                        <div class="info-value">
                          {{ withdrawalData.redeemableXCP || 0 }} xCP
                        </div>
                      </div>

                      <div
                        class="info-item"
                        v-tooltip.top="{
                          value:
                            '1 XAP = ' + withdrawalData.xap2xcpFactor + ' xCP',
                          autoHide: false,
                        }"
                      >
                        <div class="info-label">XAP Earned:</div>
                        <div class="info-value">
                          {{
                            Math.round(
                              withdrawalData.earnedXCP /
                                withdrawalData.xap2xcpFactor
                            ) || 0
                          }}
                          XAP
                        </div>
                      </div>

                      <hr class="horizontal dark my-2" />
                      <div class="info-item withdrawal-info bg-light">
                        <div class="info-label">XAP Withdrawable:</div>
                        <div class="info-value">
                          {{ withdrawalData.totalWithdrawable || 0 }} XAP
                        </div>
                      </div>
                    </div>

                    <div
                      v-if="isWithdrawalOnCooldown"
                      class="cooldown-timer-container"
                    >
                      <span v-if="isWithdrawalOnCooldown">
                        Next withdrawal in
                        <div class="countdown-timer">
                          <div class="countdown-unit">
                            <span class="time">{{
                              countdownTime.split(":")[0]
                            }}</span>
                            <span class="label">hrs</span>
                          </div>
                          <div class="countdown-separator">:</div>
                          <div class="countdown-unit">
                            <span class="time">{{
                              countdownTime.split(":")[1]
                            }}</span>
                            <span class="label">min</span>
                          </div>
                          <div class="countdown-separator">:</div>
                          <div class="countdown-unit">
                            <span class="time">{{
                              countdownTime.split(":")[2]
                            }}</span>
                            <span class="label">sec</span>
                          </div>
                        </div>
                      </span>
                      <span
                        v-else-if="countdownTime === 'Available now'"
                        class="next-withdrawal-time"
                      >
                        {{ countdownTime }}
                      </span>
                      <span v-else class="next-withdrawal-time">
                        Next withdrawal: {{ nextWithdrawalTime }}
                      </span>
                    </div>

                    <soft-button
                      v-if="!withdrawalSuccess"
                      @click="openWithdrawalConfirmation"
                      class="withdraw-btn my-2 mt-0"
                      color="primary"
                      variant="gradient"
                      size="sm"
                      fullWidth
                      :disabled="
                        isRequestingWithdrawal ||
                        isWithdrawalOnCooldown ||
                        !withdrawalData.totalWithdrawable ||
                        withdrawalData.totalWithdrawable <= 0
                      "
                    >
                      <i class="fas fa-coins me-1"></i>
                      {{
                        isRequestingWithdrawal
                          ? "Processing..."
                          : "WITHDRAW XAP"
                      }}
                    </soft-button>

                    <div v-if="withdrawalSuccess" class="success-message">
                      <i class="fas fa-check-circle me-2"></i>
                      {{ withdrawalSuccess }}
                    </div>

                    <div class="withdrawal-note">
                      <small>
                        Cap: {{ withdrawalData.withdrawalCap || "N/A" }}
                        <br />
                        Withdrawal requests are processed daily at 12 AM UTC and
                        when sufficient withdrawals are pooled.</small
                      >
                    </div>
                  </div>

                  <!-- Always show the history button regardless of state -->
                  <div class="history-btn-wrapper">
                    <button class="history-btn" @click="showWithdrawalHistory">
                      <i class="fas fa-history me-1"></i> View Withdrawal
                      History
                    </button>
                  </div>
                </div>
              </div>

              <div
                v-if="verificationStatus !== 'verified'"
                class="wallet-card verification-card"
              >
                <div class="wallet-card-header">
                  <h6>Wallet Verification</h6>
                </div>
                <div class="wallet-card-body">
                  <p class="verification-message">
                    Verify your wallet to access withdrawals and other features
                  </p>

                  <soft-button
                    @click="signAndVerify"
                    class="verify-btn"
                    color="primary"
                    variant="gradient"
                    size="sm"
                    fullWidth
                    :disabled="isVerifying"
                  >
                    <i class="fas fa-signature me-1"></i>
                    {{ isVerifying ? "Verifying..." : "VERIFY WALLET" }}
                  </soft-button>

                  <p v-if="!networkIsCorrect" class="network-warning">
                    <i class="fas fa-exclamation-triangle me-1"></i>
                    Please switch to {{ targetNetworkName }}
                  </p>

                  <p v-if="verificationError" class="verification-error">
                    {{ verificationError }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <teleport to="body">
      <Toast />
      <WalletConnectWarningModal
        :show="showWarningModal"
        @close="handleCloseWarningModal"
        @proceed="handleProceedConnect"
      />
    </teleport>

    <teleport to="body">
      <WithdrawalHistoryModal
        :show="showHistoryModal"
        :withdrawals="withdrawalHistory"
        :isLoading="isLoadingHistory"
        :error="historyError"
        :claimableBalance="externalWithdrawableBalance"
        @close="showHistoryModal = false"
        @refresh="fetchWithdrawalHistory"
      />
    </teleport>

    <teleport to="body">
      <WithdrawalConfirmationModal
        :show="showWithdrawalConfirmation"
        :data="confirmationData"
        :loading="confirmationLoading"
        @close="closeWithdrawalConfirmation"
        @confirm="confirmWithdrawal"
      />
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, onBeforeUnmount } from "vue";
import { ethers } from "ethers";
import { useToast } from "primevue/usetoast";
import SoftButton from "@/components/SoftButton.vue";
import WalletConnectWarningModal from "@/components/WalletConnectWarningModal.vue";
import WithdrawalHistoryModal from "@/components/WithdrawalHistoryModal.vue";
import WithdrawalConfirmationModal from "@/components/WithdrawalConfirmationModal.vue";
import config from "@/config.js";
import store from "@/store";

// Simple ERC20 ABI - just what we need for balanceOf
const ERC20_ABI = [
  // balanceOf
  {
    constant: true,
    inputs: [{ name: "_owner", type: "address" }],
    name: "balanceOf",
    outputs: [{ name: "balance", type: "uint256" }],
    type: "function",
  },
  // decimals
  {
    constant: true,
    inputs: [],
    name: "decimals",
    outputs: [{ name: "", type: "uint8" }],
    type: "function",
  },
  // symbol
  {
    constant: true,
    inputs: [],
    name: "symbol",
    outputs: [{ name: "", type: "string" }],
    type: "function",
  },
];

// Emits event when wallet is successfully verified
const emit = defineEmits(["wallet-verified"]);

// Initialize PrimeVue Toast
const toast = useToast();

// State variables
const provider = ref(null);
const signer = ref(null);
const connected = ref(false);
const account = ref(null);
const network = ref(null);
const isVerifying = ref(false);
const verificationStatus = ref("idle"); // idle, verifying, verified, error
const verificationError = ref(null);
const showDropdown = ref(false);

// State for the warning modal
const showWarningModal = ref(false);
const currentWalletType = ref(null); // 'ethereum' or 'solana'

// Wallet detection states
const hasMetaMask = ref(false);
const hasPhantom = ref(false);

// Balance state
const ethBalance = ref("0");
const tokenBalance = ref("0");
const isLoadingBalances = ref(false);

// Withdrawal state
const withdrawalData = ref({
  totalWithdrawable: 0,
  redeemableXCP: 0,
  earnedXCP: 0,
  redeemedXCP: 0,
  xap2xcpFactor: 0,
  message: "",
  withdrawalCap: "",
  nextWithdrawal: null,
  tgTapEarnings: 0,
  tgTapEarningsToXCP: 0,
  tgTapEarningsToXAP: 0,
});
const isLoadingWithdrawal = ref(false);
const isRequestingWithdrawal = ref(false);
const withdrawalError = ref(null);
const withdrawalSuccess = ref(null);
const countdownTime = ref("");
const countdownTimer = ref(null);

// External withdrawal site balance
const externalWithdrawableBalance = ref(0);
const isLoadingExternalBalance = ref(false);
const externalBalanceError = ref(null);

// Withdrawal history state
const withdrawalHistory = ref([]);
const isLoadingHistory = ref(false);
const showHistoryModal = ref(false);
const historyError = ref(null);

// State for withdrawal confirmation dialog
const showWithdrawalConfirmation = ref(false);
const confirmationLoading = ref(false);
const confirmationData = ref({
  xapAmount: 0,
  xcpToRedeem: 0,
  processingTime: "12 AM UTC",
  claimAvailableAfter: "",
  nextWithdrawalTime: "",
});

// Configuration
const targetNetworkId =
  store.state.NETWORK_NAME === "mainnet" ? "0x1" : "0xaa36a7"; // Mainnet (0x1) or Sepolia (0xaa36a7)
const targetNetworkName = store.state.NETWORK_NAME;
const backendBaseUrl = store.state.SERVER_URL;
const XAP_CONTRACT = store.state.XAP_CONTRACT;
const XAP_DECIMALS = 8;

// Computed properties
const shortAddress = computed(() => {
  if (!account.value) return "";
  return `${account.value.substring(0, 7)}...${account.value.substring(
    account.value.length - 5
  )}`;
});

const networkName = computed(() => {
  if (!network.value) return "Unknown";
  switch (network.value.chainId.toString()) {
    case "1": // Hex 0x1
      return "Ethereum Mainnet";
    case "11155111": // Hex 0xaa36a7
      return "Sepolia Testnet";
    default:
      return `Unknown (${network.value.chainId})`;
  }
});

const networkIsCorrect = computed(() => {
  if (!network.value) return false;
  return (
    network.value.chainId.toString() ===
    parseInt(targetNetworkId, 16).toString()
  );
});

// Computed properties for withdrawal
const isWithdrawalOnCooldown = computed(() => {
  if (!withdrawalData.value.nextWithdrawal) return false;
  return Date.now() < withdrawalData.value.nextWithdrawal;
});

// Computed property for formatted next withdrawal time
const nextWithdrawalTime = computed(() => {
  if (!withdrawalData.value.nextWithdrawal) return null;

  const date = new Date(withdrawalData.value.nextWithdrawal);
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
});

// Methods
const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;

  // Check wallet availability whenever dropdown is opened
  if (showDropdown.value) {
    checkWalletAvailability();
  }
};

const closeDropdownOnClickOutside = (event) => {
  const container = event.target.closest(".wallet-connect-container");
  if (!container) {
    showDropdown.value = false;
  }
};

// New method to show the warning modal
const initiateConnectWallet = (walletType) => {
  // Don't proceed if wallet is not available
  if (
    (walletType === "ethereum" && !hasMetaMask.value) ||
    (walletType === "solana" && !hasPhantom.value)
  ) {
    const walletName = walletType === "ethereum" ? "MetaMask" : "Phantom";
    toast.add({
      severity: "warn",
      summary: "Wallet Required",
      detail: `${walletName} wallet is not installed. Please install it first.`,
      life: 3000,
    });
    return;
  }

  currentWalletType.value = walletType;
  showWarningModal.value = true;
};

const handleProceedConnect = () => {
  showWarningModal.value = false;
  if (currentWalletType.value === "ethereum") {
    proceedWithConnectWallet();
  } else if (currentWalletType.value === "solana") {
    connectPhantomWallet();
  }
};

const handleCloseWarningModal = () => {
  showWarningModal.value = false;
  currentWalletType.value = null;
};

const connectWallet = async () => {
  // This is a legacy function - we now use initiateConnectWallet
  initiateConnectWallet("ethereum");
};

const connectPhantomWallet = async () => {
  try {
    if (!window.solana || !window.solana.isPhantom) {
      throw new Error("Phantom wallet not found");
    }

    // Request connection to Phantom
    const resp = await window.solana.connect();
    const publicKey = resp.publicKey.toString();

    // Here you would implement phantom-specific connection logic
    // For now, we'll just show a notification
    toast.add({
      severity: "success",
      summary: "Connected to Phantom",
      detail: `Connected to Phantom: ${publicKey.substring(
        0,
        6
      )}...${publicKey.substring(publicKey.length - 4)}`,
      life: 3000,
    });

    // For Solana integration, you would need to implement the corresponding
    // balance checking, network validation, and verification processes
  } catch (error) {
    console.error("Error connecting Phantom wallet:", error);
    toast.add({
      severity: "error",
      summary: "Connection Failed",
      detail: `Error: ${error.message || "Failed to connect Phantom wallet"}`,
      life: 3000,
    });
  }
};

const proceedWithConnectWallet = async () => {
  if (typeof window.ethereum !== "undefined") {
    try {
      const web3Provider = new ethers.providers.Web3Provider(window.ethereum);
      await web3Provider.send("eth_requestAccounts", []);
      const web3Signer = web3Provider.getSigner();
      const currentAccount = await web3Signer.getAddress();
      const networkData = await web3Provider.getNetwork();

      provider.value = web3Provider;
      signer.value = web3Signer;
      account.value = currentAccount;
      network.value = networkData;
      connected.value = true;
      verificationStatus.value = "idle";
      verificationError.value = null;

      // Save connection state
      saveWalletState();

      // Fetch balances after successful connection
      await fetchBalances();

      // Check network and show appropriate notification
      if (!networkIsCorrect.value) {
        toast.add({
          severity: "warn",
          summary: "Network Warning",
          detail: `Please switch to ${targetNetworkName} network.`,
          life: 3000,
        });
        switchNetwork();
      } else {
        toast.add({
          severity: "success",
          summary: "Connected",
          detail: `Connected to ${shortAddress.value}`,
          life: 3000,
        });
      }

      // Listen for events
      window.ethereum.on("accountsChanged", handleAccountsChanged);
      window.ethereum.on("chainChanged", handleChainChanged);
    } catch (error) {
      console.error("Error connecting wallet:", error);
      toast.add({
        severity: "error",
        summary: "Connection Failed",
        detail: `Error: ${error.message || "User rejected connection"}`,
        life: 3000,
      });
      disconnectWallet();
    }
  } else {
    toast.add({
      severity: "warn",
      summary: "Wallet Not Found",
      detail: "MetaMask or another Ethereum wallet not found",
      life: 3000,
    });
  }
};

// Other methods remain mostly the same
const checkNetwork = async () => {
  if (!networkIsCorrect.value) {
    await switchNetwork();
  }
};

const switchNetwork = async () => {
  if (!provider.value) return;
  try {
    await window.ethereum.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: targetNetworkId }],
    });
  } catch (switchError) {
    if (switchError.code === 4902) {
      console.error("Error switching network:", switchError);
      toast.add({
        severity: "error",
        summary: "Network Error",
        detail: `Network not available. Please add ${targetNetworkName} to your wallet.`,
        life: 3000,
      });
    } else {
      console.error("Error switching network:", switchError);
      toast.add({
        severity: "warn",
        summary: "Network Error",
        detail: `Please manually switch to ${targetNetworkName} in your wallet.`,
        life: 3000,
      });
    }
  }
};

const signAndVerify = async () => {
  if (!account.value) {
    toast.add({
      severity: "warn",
      summary: "Verification Error",
      detail: "Wallet not connected.",
      life: 3000,
    });
    return;
  }

  if (!networkIsCorrect.value) {
    toast.add({
      severity: "warn",
      summary: "Network Required",
      detail: `Please switch to ${targetNetworkName} before verifying.`,
      life: 3000,
    });
    await switchNetwork();
    return;
  }

  isVerifying.value = true;
  verificationStatus.value = "verifying";
  verificationError.value = null;

  try {
    // Get JWT token
    const jwtToken = localStorage.getItem("jwt");
    if (!jwtToken) {
      throw new Error("Authentication token not found.");
    }

    // Fetch Nonce from backend
    const nonceResponse = await fetch(`${backendBaseUrl}/auth/web3/nonce`, {
      method: "POST",
      headers: {
        Authorization: jwtToken,
        "Content-Type": "application/json",
      },
    });

    if (!nonceResponse.ok) {
      const errorData = await nonceResponse.json();
      throw new Error(
        errorData.message ||
          `Failed to fetch nonce (Status: ${nonceResponse.status})`
      );
    }

    const { nonce } = await nonceResponse.json();
    if (!nonce) {
      throw new Error("Nonce not received from backend.");
    }

    // Create a fresh provider and signer to avoid proxy issues
    if (typeof window.ethereum === "undefined") {
      throw new Error("Ethereum provider not found");
    }

    const freshProvider = new ethers.providers.Web3Provider(window.ethereum);
    const freshSigner = freshProvider.getSigner();

    // Sign the nonce
    const messageToSign = `${nonce}`;
    const signature = await freshSigner.signMessage(messageToSign);

    // Verify Signature with backend
    const verifyResponse = await fetch(`${backendBaseUrl}/auth/web3/verify`, {
      method: "POST",
      headers: {
        Authorization: jwtToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        signature: signature,
        publicAddress: account.value,
      }),
    });

    const verifyResult = await verifyResponse.json();

    if (!verifyResponse.ok) {
      throw new Error(
        verifyResult.message ||
          `Verification failed (Status: ${verifyResponse.status})`
      );
    }

    // Success
    verificationStatus.value = "verified";
    toast.add({
      severity: "success",
      summary: "Verification Success",
      detail: "Wallet verified successfully!",
      life: 3000,
    });
    emit("wallet-verified", { address: account.value });

    // Save verified state to localStorage
    saveWalletState(true);

    // Hide dropdown after a delay
    setTimeout(() => {
      showDropdown.value = false;
    }, 1500);
  } catch (error) {
    console.error("Verification error:", error);
    verificationStatus.value = "error";
    verificationError.value =
      error.message || "An unknown verification error occurred.";

    toast.add({
      severity: "error",
      summary: "Verification Error",
      detail: `Verification failed: ${verificationError.value}`,
      life: 3000,
    });
  } finally {
    isVerifying.value = false;
  }
};

const handleAccountsChanged = (accounts) => {
  if (accounts.length === 0) {
    toast.add({
      severity: "warn",
      summary: "Wallet Update",
      detail: "Wallet disconnected or locked.",
      life: 3000,
    });
    disconnectWallet();
  } else if (accounts[0] !== account.value) {
    account.value = accounts[0];
    toast.add({
      severity: "info",
      summary: "Account Changed",
      detail: `Account switched to ${shortAddress.value}`,
      life: 3000,
    });
    verificationStatus.value = "idle";
    verificationError.value = null;
    checkNetwork();

    // Refresh balances when account changes
    fetchBalances();
  }
};

const handleChainChanged = async (chainId) => {
  if (typeof window.ethereum !== "undefined") {
    const web3Provider = new ethers.providers.Web3Provider(window.ethereum);
    provider.value = web3Provider;
    signer.value = web3Provider.getSigner();
    const currentNetwork = await web3Provider.getNetwork();
    network.value = currentNetwork;

    toast.add({
      severity: "info",
      summary: "Network Changed",
      detail: `Network changed to ${networkName.value}`,
      life: 3000,
    });
    verificationStatus.value = "idle";
    verificationError.value = null;

    // Refresh balances when network changes
    fetchBalances();

    if (!networkIsCorrect.value) {
      toast.add({
        severity: "warn",
        summary: "Wrong Network",
        detail: `Please switch to ${targetNetworkName} network`,
        life: 3000,
      });
    }
  } else {
    disconnectWallet();
  }
};

const disconnectWallet = () => {
  if (window.ethereum && window.ethereum.removeListener) {
    window.ethereum.removeListener("accountsChanged", handleAccountsChanged);
    window.ethereum.removeListener("chainChanged", handleChainChanged);
  }

  provider.value = null;
  signer.value = null;
  connected.value = false;
  account.value = null;
  network.value = null;
  verificationStatus.value = "idle";
  verificationError.value = null;
  showDropdown.value = false;

  // Remove from localStorage
  localStorage.removeItem("wallet_connected");
  localStorage.removeItem("wallet_verified");

  toast.add({
    severity: "info",
    summary: "Disconnected",
    detail: "Wallet disconnected",
    life: 3000,
  });
};

// Lifecycle hooks
onMounted(async () => {
  document.addEventListener("click", closeDropdownOnClickOutside);

  // Check wallet availability on mount
  checkWalletAvailability();

  // Try to automatically reconnect if wallet info exists in localStorage
  const savedWallet = localStorage.getItem("wallet_connected");
  if (savedWallet) {
    try {
      // Only attempt reconnect if wallet extension is available
      if (typeof window.ethereum !== "undefined") {
        // Check if the user is already connected to their wallet
        const accounts = await window.ethereum.request({
          method: "eth_accounts",
        });
        if (accounts && accounts.length > 0) {
          console.log("Restoring wallet connection from localStorage");

          const web3Provider = new ethers.providers.Web3Provider(
            window.ethereum
          );
          const web3Signer = web3Provider.getSigner();
          const currentAccount = await web3Signer.getAddress();
          const networkData = await web3Provider.getNetwork();

          provider.value = web3Provider;
          signer.value = web3Signer;
          account.value = currentAccount;
          network.value = networkData;
          connected.value = true;

          // Check if wallet was verified (stored in localStorage)
          if (localStorage.getItem("wallet_verified") === "true") {
            verificationStatus.value = "verified";
            // Fetch withdrawal info if verified
            fetchWithdrawableAmount();
          } else {
            verificationStatus.value = "idle";
          }

          // Set up event listeners
          window.ethereum.on("accountsChanged", handleAccountsChanged);
          window.ethereum.on("chainChanged", handleChainChanged);

          // Check network silently - don't show notification on initial load
          if (!networkIsCorrect.value) {
            console.log(
              `Connected to incorrect network. Expected ${targetNetworkName}.`
            );
          }

          // Fetch balances after reconnecting
          await fetchBalances();
        }
      }
    } catch (error) {
      console.error("Error restoring wallet connection:", error);
      // Clear localStorage if reconnection fails
      localStorage.removeItem("wallet_connected");
      localStorage.removeItem("wallet_verified");
    }
  }
});

// Function to save wallet state to localStorage
const saveWalletState = (isVerified = false) => {
  if (connected.value) {
    localStorage.setItem("wallet_connected", "true");
    if (isVerified) {
      localStorage.setItem("wallet_verified", "true");
    }
  } else {
    localStorage.removeItem("wallet_connected");
    localStorage.removeItem("wallet_verified");
  }
};

onBeforeUnmount(() => {
  document.removeEventListener("click", closeDropdownOnClickOutside);

  // Clean up event listeners
  if (window.ethereum && window.ethereum.removeListener) {
    window.ethereum.removeListener("accountsChanged", handleAccountsChanged);
    window.ethereum.removeListener("chainChanged", handleChainChanged);
  }

  // Clear countdown timer
  if (countdownTimer.value) {
    clearInterval(countdownTimer.value);
    countdownTimer.value = null;
  }
});

// Watch for verification status changes
watch(verificationStatus, (newStatus, oldStatus) => {
  if (newStatus === "verified" && oldStatus !== "verified") {
    // Automatically fetch withdrawal info when wallet is verified
    fetchWithdrawableAmount();
  }
});

// Start countdown timer
const startCountdownTimer = () => {
  // Clear any existing timer
  if (countdownTimer.value) {
    clearInterval(countdownTimer.value);
  }

  // Only start if we have a nextWithdrawal timestamp and it's in the future
  if (withdrawalData.value.nextWithdrawal && isWithdrawalOnCooldown.value) {
    // Update immediately
    updateCountdown();

    // Then update every second
    countdownTimer.value = setInterval(() => {
      updateCountdown();
    }, 1000);
  }
};

// Update countdown display
const updateCountdown = () => {
  if (!withdrawalData.value.nextWithdrawal) {
    countdownTime.value = "";
    return;
  }

  const now = Date.now();
  const target = withdrawalData.value.nextWithdrawal;
  const difference = target - now;

  if (difference <= 0) {
    // Countdown completed
    countdownTime.value = "Available now";
    clearInterval(countdownTimer.value);

    // Refresh withdrawal data
    fetchWithdrawableAmount();
    return;
  }

  // Calculate hours, minutes, seconds
  const hours = Math.floor(difference / (1000 * 60 * 60));
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((difference % (1000 * 60)) / 1000);

  // Format with leading zeros
  const formattedHours = String(hours).padStart(2, "0");
  const formattedMinutes = String(minutes).padStart(2, "0");
  const formattedSeconds = String(seconds).padStart(2, "0");

  // Set the countdown display
  countdownTime.value = `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

// Watch for withdrawal data changes to update timer
watch(
  () => withdrawalData.value.nextWithdrawal,
  (newVal) => {
    if (newVal) {
      startCountdownTimer();
    }
  }
);

// Withdrawal methods
const fetchWithdrawableAmount = async () => {
  withdrawalError.value = null;
  withdrawalSuccess.value = null;
  isLoadingWithdrawal.value = true;

  try {
    // Get JWT token
    const jwtToken = localStorage.getItem("jwt");
    if (!jwtToken) {
      throw new Error("Authentication token not found.");
    }

    const response = await fetch(
      `${backendBaseUrl}/web3/calculateWithdrawable/`,
      {
        method: "GET",
        headers: {
          Authorization: jwtToken,
          "Content-Type": "application/json",
        },
      }
    );

    // Parse the response once, regardless of status
    const data = await response.json();

    // Update withdrawalData with all available fields from the response, even in error cases
    // This ensures fields like nextWithdrawal, withdrawalCap, XCP stats are updated.
    if (data) {
      withdrawalData.value = {
        ...withdrawalData.value, // Keep existing values if not overwritten
        ...data, // Overwrite with new data from API
      };
    }

    // Handle error responses
    if (!response.ok) {
      withdrawalError.value =
        data.message ||
        `Failed to fetch withdrawable amount (Status: ${response.status})`;
      // If the error indicates zero withdrawable, explicitly set these for clarity
      if (data.message && data.message.includes("You have 0")) {
        withdrawalData.value.totalWithdrawable = 0;
        withdrawalData.value.redeemableXCP = 0;
      }
    } else {
      // Handle success case - data is already assigned
      withdrawalError.value = null; // Clear any previous error
    }

    // Start countdown timer if nextWithdrawal is available, irrespective of error/success for other data
    if (withdrawalData.value.nextWithdrawal) {
      startCountdownTimer();
    }

    // After fetching withdrawal data, also fetch external balance
    fetchExternalWithdrawableBalance();
  } catch (error) {
    console.error("Error fetching withdrawable amount:", error);
    // If the error is not from the API response (e.g., network error)
    if (!withdrawalError.value) {
      withdrawalError.value =
        error.message || "Failed to load withdrawal information";
    }
  } finally {
    isLoadingWithdrawal.value = false;
  }
};

const fetchExternalWithdrawableBalance = async () => {
  isLoadingExternalBalance.value = true;
  externalBalanceError.value = null;

  try {
    // Get JWT token
    const jwtToken = localStorage.getItem("jwt");
    if (!jwtToken) {
      throw new Error("Authentication token not found.");
    }

    const response = await fetch(`${backendBaseUrl}/web3/availableToWithdraw`, {
      method: "GET",
      headers: {
        Authorization: jwtToken,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message ||
          `Failed to fetch external withdrawable balance (Status: ${response.status})`
      );
    }

    const data = await response.json();
    externalWithdrawableBalance.value = data.withdrawableBalance || 0;
  } catch (error) {
    console.error("Error fetching external withdrawable balance:", error);
    externalBalanceError.value = error.message;
  } finally {
    isLoadingExternalBalance.value = false;
  }
};

const openWithdrawalConfirmation = () => {
  // Calculate some values for confirmation
  confirmationData.value = {
    xapAmount: withdrawalData.value.totalWithdrawable || 0,
    xcpToRedeem:
      (withdrawalData.value.totalWithdrawable || 0) *
      (withdrawalData.value.xap2xcpFactor || 0),
    processingTime: "12 AM UTC",
    claimAvailableAfter: "approximately 24 hours after processing",
    nextWithdrawalTime: "48 hours after this withdrawal",
  };
  showWithdrawalConfirmation.value = true;
};

const closeWithdrawalConfirmation = () => {
  showWithdrawalConfirmation.value = false;
};

const confirmWithdrawal = async () => {
  closeWithdrawalConfirmation();
  await requestWithdrawal();
};

const requestWithdrawal = async () => {
  if (isWithdrawalOnCooldown.value) {
    toast.add({
      severity: "warn",
      summary: "Withdrawal Restricted",
      detail: "You are still on cooldown period for withdrawals",
      life: 3000,
    });
    return;
  }

  withdrawalError.value = null;
  withdrawalSuccess.value = null;
  isRequestingWithdrawal.value = true;

  try {
    // Get JWT token
    const jwtToken = localStorage.getItem("jwt");
    if (!jwtToken) {
      throw new Error("Authentication token not found.");
    }

    const response = await fetch(`${backendBaseUrl}/web3/withdrawXAP`, {
      method: "POST",
      headers: {
        Authorization: jwtToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        walletAddress: account.value,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message ||
          `Withdrawal request failed (Status: ${response.status})`
      );
    }

    const data = await response.json();

    // Set success message from this response
    withdrawalSuccess.value =
      data.message || "Withdrawal request submitted successfully.";

    toast.add({
      severity: "success",
      summary: "Withdrawal Requested",
      detail: withdrawalSuccess.value,
      life: 3000,
    });

    // Refresh all withdrawal data from the authoritative source
    // This will update totalWithdrawable, nextWithdrawal, etc.
    // and also trigger the countdown timer update if applicable.
    await fetchWithdrawableAmount();
  } catch (error) {
    console.error("Error requesting withdrawal:", error);
    withdrawalError.value =
      error.message || "Failed to submit withdrawal request";

    toast.add({
      severity: "error",
      summary: "Withdrawal Failed",
      detail: withdrawalError.value,
      life: 3000,
    });
  } finally {
    isRequestingWithdrawal.value = false;
  }
};

// Check wallet availability
const checkWalletAvailability = () => {
  // Check for MetaMask
  hasMetaMask.value =
    typeof window.ethereum !== "undefined" &&
    (window.ethereum.isMetaMask ||
      (window.ethereum.providers &&
        window.ethereum.providers.some((p) => p.isMetaMask)));

  // Check for Phantom
  hasPhantom.value = window.solana && window.solana.isPhantom;
};

// Get the appropriate contract address based on network
const getTokenContractAddress = () => {
  return store.state.XAP_CONTRACT;
};

// Fetch balances
const fetchBalances = async () => {
  if (!connected.value || !account.value) return;

  try {
    isLoadingBalances.value = true;

    // Create fresh provider to avoid proxy issues
    if (typeof window.ethereum === "undefined") {
      throw new Error("Ethereum provider not found");
    }

    const freshProvider = new ethers.providers.Web3Provider(window.ethereum);

    // Fetch ETH balance
    const balance = await freshProvider.getBalance(account.value);
    ethBalance.value = parseFloat(ethers.utils.formatEther(balance)).toFixed(4);

    // Fetch XAP token balance
    const tokenContractAddress = getTokenContractAddress();
    const tokenContract = new ethers.Contract(
      tokenContractAddress,
      ERC20_ABI,
      freshProvider
    );

    const rawTokenBalance = await tokenContract.balanceOf(account.value);
    // Format with 8 decimals (XAP specific)
    tokenBalance.value = parseFloat(
      ethers.utils.formatUnits(rawTokenBalance, XAP_DECIMALS)
    ).toFixed(2);
  } catch (error) {
    console.error("Error fetching balances:", error);
    ethBalance.value = "Error";
    tokenBalance.value = "Error";
  } finally {
    isLoadingBalances.value = false;
  }
  // Try to fetch external balance even if there was an error
  fetchExternalWithdrawableBalance();
};

// Withdrawal history methods
const showWithdrawalHistory = () => {
  showHistoryModal.value = true;
  fetchWithdrawalHistory();
};

const fetchWithdrawalHistory = async () => {
  isLoadingHistory.value = true;
  historyError.value = null;

  try {
    // Get JWT token
    const jwtToken = localStorage.getItem("jwt");
    if (!jwtToken) {
      throw new Error("Authentication token not found.");
    }

    const response = await fetch(`${backendBaseUrl}/web3/withdrawalRequests`, {
      method: "GET",
      headers: {
        Authorization: jwtToken,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message ||
          `Failed to fetch withdrawal history (Status: ${response.status})`
      );
    }

    const data = await response.json();
    withdrawalHistory.value = data;
  } catch (error) {
    console.error("Error fetching withdrawal history:", error);
    historyError.value = error.message || "Failed to load withdrawal history";
  } finally {
    isLoadingHistory.value = false;
  }
};
</script>

<style scoped>
.wallet-connect-container {
  position: relative;
  display: inline-block;
}

.wallet-connect-link {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  text-decoration: none;
  color: #344767;
  background-color: #f8f9fa;
  transition: all 0.15s ease-in;
  white-space: nowrap;
}

.wallet-connect-link:hover {
  background-color: #e9ecef;
}

.wallet-connected {
  background-color: rgba(94, 114, 228, 0.1);
  color: #5e72e4;
}

.wallet-connected:hover {
  background-color: rgba(94, 114, 228, 0.2);
}

/* Dropdown Styling */
.wallet-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  width: 650px;
  margin-top: 0.5rem;
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 10px 25px rgba(50, 50, 93, 0.1), 0 5px 15px rgba(0, 0, 0, 0.07);
  z-index: 1000;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.05);
  animation: dropdown-appear 0.25s ease-out;
}

@keyframes dropdown-appear {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.wallet-dropdown-header {
  padding: 1rem 1.25rem;
  background: linear-gradient(to right, #f8f9fa, #f1f3f9);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.wallet-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.wallet-header-left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.wallet-dropdown-title {
  font-weight: 600;
  font-size: 1rem;
  color: #344767;
}

.header-disconnect-btn {
  background: none;
  border: none;
  color: #8392ab;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  margin-left: 0.5rem;
  flex-shrink: 0;
}

.header-disconnect-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
  color: #f5365c;
}

.wallet-address {
  font-weight: 600;
  font-size: 0.9rem;
  color: #344767;
  margin-bottom: 0.25rem;
}

.wallet-dropdown-body {
  padding: 1.25rem;
}

/* Card Styling */
.wallet-card {
  background-color: #fff;
  border-radius: 0.75rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.05);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.wallet-card-header {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  background-color: #f8f9fa;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.wallet-card-header h6 {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #344767;
}

.wallet-card-body {
  padding: 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Network Badge */
.network-badge {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  border-radius: 1rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
}

.network-badge-success {
  background-color: rgba(45, 206, 137, 0.1);
  color: #2dce89;
}

.network-badge-warning {
  background-color: rgba(251, 99, 64, 0.1);
  color: #fb6340;
}

/* Balance Items */
.balance-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.03);
  width: 100%;
  overflow: hidden;
}

.balance-item:last-child {
  border-bottom: none;
}

.balance-icon-wrapper {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #f1f3f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.balance-icon {
  color: #5e72e4;
  font-size: 1rem;
}

.balance-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0; /* Important for text overflow */
  overflow: hidden;
}

.balance-label {
  font-size: 0.9rem;
  font-weight: 600;
  /* color: #8392ab; */
  display: flex;
  align-items: center;
}

.balance-value {
  font-size: 1.2rem;
  font-weight: 600;
  color: #344767;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Withdrawal Info */
.info-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 0.375rem;
  overflow: hidden;
}

.info-item.withdrawal-info {
  border: 1px dotted #111214;
  background-color: #f8f9fa;

  border-radius: 0.375rem;
  overflow: hidden;
}

.info-label {
  font-size: 0.75rem;
  color: #8392ab;
  margin-right: 0.5rem;
  flex-shrink: 0;
}

.info-value {
  font-size: 0.85rem;
  font-weight: 600;
  color: #344767;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Claimable Balance */
.claim-alert {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: rgba(94, 114, 228, 0.05);
  border: 1px dashed rgba(54, 18, 255, 0.3);
  border-radius: 0.5rem;
}

.claim-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(94, 114, 228, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.claim-icon i {
  color: #5e72e4;
  font-size: 1rem;
}

.claim-details {
  flex: 1;
}

.claim-amount {
  font-size: 1.1rem;
  font-weight: 700;
  color: #344767;
}

.currency {
  font-size: 0.75rem;
  color: #8392ab;
  margin-left: 0.2rem;
}

.claim-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #5e72e4;
  margin-bottom: 0.25rem;
}

.claim-message {
  font-size: 0.7rem;
  color: #8392ab;
  margin-bottom: 0.5rem;
}

/* Cooldown Timer */
.cooldown-timer-container {
  background-color: rgba(251, 99, 64, 0.05);
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin: 0.75rem 0;
  text-align: center;
  font-size: 0.8rem;
  color: #fb6340;
}

.cooldown-timer-container i {
  margin-right: 0.25rem;
}

.countdown-timer {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 0.5rem;
}

.countdown-unit {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.countdown-unit .time {
  background-color: rgba(251, 99, 64, 0.1);
  color: #fb6340;
  font-weight: 700;
  font-size: 0.9rem;
  padding: 0.2rem 0.3rem;
  border-radius: 0.25rem;
  min-width: 1.75rem;
  text-align: center;
}

.countdown-unit .label {
  font-size: 0.6rem;
  margin-top: 0.1rem;
  color: #8392ab;
  text-transform: lowercase;
}

.countdown-separator {
  font-weight: 700;
  font-size: 0.9rem;
  margin: 0 0.1rem;
  color: #fb6340;
  align-self: flex-start;
  margin-top: 0.2rem;
}

.next-withdrawal-time {
  font-size: 0.8rem;
  font-weight: 600;
}

/* Button Styles */
.refresh-icon-btn {
  background: none;
  border: none;
  color: #8392ab;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0.25rem;
}

.refresh-icon-btn:hover {
  color: #5e72e4;
  transform: rotate(15deg);
}

.refresh-icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.withdraw-btn {
  margin-top: 0.5rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.verify-btn {
  margin-top: 0.5rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.history-btn-wrapper {
  margin-top: auto;
  text-align: center;
  padding-top: 0.75rem;
  border-top: 1px dashed rgba(0, 0, 0, 0.05);
}

.history-btn {
  background: none;
  border: none;
  color: #5e72e4;
  font-size: 0.8rem;
  font-weight: 500;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
}

.history-btn:hover {
  background-color: rgba(94, 114, 228, 0.1);
  transform: translateY(-1px);
}

/* Status Messages */
.loading-indicator,
.error-message,
.success-message {
  align-items: center;
  text-align: center;
  padding: 0.75rem;
  margin: 0.5rem 0;
  border-radius: 0.5rem;
  font-size: 0.8rem;
}

.loading-indicator {
  color: #8392ab;
  background-color: #f8f9fa;
}

.error-message {
  color: #f5365c;
  /* background-color: rgba(245, 54, 92, 0.05); */
}

.success-message {
  color: #2dce89;
  background-color: rgba(45, 206, 137, 0.05);
}

.network-warning,
.verification-error {
  font-size: 0.75rem;
  color: #fb6340;
  margin-top: 0.75rem;
  text-align: center;
}

.verification-message {
  text-align: center;
  font-size: 0.85rem;
  color: #8392ab;
  margin-bottom: 1rem;
}

.withdrawal-note {
  text-align: center;
  font-size: 0.7rem;
  color: #8392ab;
  margin-top: 0.75rem;
}

/* Wallet Selection */
.wallet-selection {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.wallet-option {
  display: flex;
  align-items: center;
  padding: 1rem;
  border-radius: 0.75rem;
  background-color: #f8f9fa;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.wallet-option:hover {
  background-color: #f1f3f9;
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
}

.wallet-option-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.wallet-option-disabled:hover {
  background-color: #f8f9fa;
  transform: none;
  box-shadow: none;
}

.wallet-icon {
  font-size: 1.75rem;
  width: 40px;
  text-align: center;
  margin-right: 1rem;
  color: #5e72e4;
}

.wallet-option-info {
  display: flex;
  flex-direction: column;
}

.wallet-name {
  font-weight: 600;
  font-size: 1rem;
  color: #344767;
}

.wallet-status {
  font-size: 0.75rem;
  color: #5e72e4;
}

.wallet-status-error {
  font-size: 0.75rem;
  color: #fb6340;
}

.wallet-message {
  padding: 0.75rem;
  border-radius: 0.5rem;
  background-color: #f8f9fa;
  margin-top: 0.5rem;
}

.wallet-no-wallets {
  color: #fb6340;
  font-size: 0.8rem;
  margin-bottom: 0;
  line-height: 1.4;
}

.wallet-no-wallets a {
  color: #5e72e4;
  text-decoration: none;
  font-weight: 600;
}

.wallet-no-wallets a:hover {
  text-decoration: underline;
}

.wallet-description {
  font-size: 0.8rem;
  color: #8392ab;
  margin-bottom: 0;
}

/* Responsive Adjustments */
@media (max-width: 576px) {
  .wallet-dropdown {
    width: 90vw;
    max-width: 320px;
    max-height: 85vh;
    overflow-y: auto;
  }

  .wallet-dropdown-body {
    padding: 1rem;
  }

  .wallet-card-body {
    padding: 0.75rem;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .balance-value {
    font-size: 0.9rem;
  }

  .claim-alert {
    flex-direction: column;
    text-align: center;
  }

  .claim-icon {
    margin-right: 0;
    margin-bottom: 0.5rem;
  }

  .wallet-icon {
    font-size: 1.5rem;
    width: 32px;
    margin-right: 0.75rem;
  }

  .wallet-name {
    font-size: 0.9rem;
  }

  .countdown-unit .time {
    font-size: 0.8rem;
    padding: 0.15rem 0.25rem;
  }

  /* Handle XAP balance display on mobile */
  .info-item {
    flex-wrap: wrap;
  }

  .info-value {
    width: 100%;
    text-align: left;
    margin-top: 0.25rem;
  }

  /* For very large numbers, especially in the ETH/XAP balance display */
  .balance-value {
    font-family: monospace; /* Use monospace font for better number rendering */
    font-size: 0.85rem; /* Slightly smaller font */
    overflow-x: auto; /* Allow horizontal scrolling within the number if needed */
    white-space: nowrap;
    scrollbar-width: none; /* Hide scrollbar in Firefox */
    max-width: 100%;
  }

  .balance-value::-webkit-scrollbar {
    display: none; /* Hide scrollbar in Chrome/Safari */
  }
}

/* For medium-sized screens */
@media (max-width: 768px) and (min-width: 577px) {
  .wallet-dropdown {
    width: 90vw;
    max-width: 450px;
  }

  .balance-value {
    font-size: 0.95rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

/* XCP Stats Styling */
.xcp-stats-container {
  background-color: rgba(94, 114, 228, 0.05);
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin: 0.75rem 0;
  border: 1px dashed rgba(94, 114, 228, 0.2);
}

.xcp-stats-header {
  font-size: 0.85rem;
  font-weight: 600;
  color: #5e72e4;
  margin-bottom: 0.75rem;
  text-align: center;
}

.xcp-stats-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
}

.xcp-stats-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0.5rem;
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 0.375rem;
}

.stats-label {
  font-size: 0.75rem;
  color: #8392ab;
  margin-right: 0.5rem;
}

.stats-value {
  font-size: 0.8rem;
  font-weight: 600;
  color: #344767;
  text-align: right;
}

@media (max-width: 576px) {
  .xcp-stats-item {
    flex-wrap: wrap;
  }

  .stats-value {
    width: 100%;
    text-align: left;
    margin-top: 0.25rem;
  }
}

/* :deep(.earnings-tooltip) {
  width: 320px !important;
  max-width: 320px !important;
  background-color: #344767 !important;
  color: #fff !important;
  border-radius: 0.5rem !important;
  padding: 0 !important;
  opacity: 1 !important;
}

:deep(.earnings-tooltip .p-tooltip-arrow) {
  border-top-color: #344767 !important;
}

:deep(.breakdown-grid) {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 0.35rem 1.5rem;
}

:deep(.text-end) {
  text-align: right;
} */
</style>
